import React, { useState, useEffect } from 'react';
import { X, MapPin, Clock, DollarSign, Users, Calendar, ExternalLink, BookmarkPlus, Bookmark, Send } from 'lucide-react';
import { getJobById, saveJob, unsaveJob } from '../../../services/jobService';
import ApplicationModal from './ApplicationModal';

const JobDetailModal = ({ job: initialJob, isOpen, onClose }) => {
  const [job, setJob] = useState(initialJob);
  const [loading, setLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  useEffect(() => {
    if (isOpen && initialJob?.id) {
      fetchJobDetails();
    }
  }, [isOpen, initialJob?.id]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const jobDetails = await getJobById(initialJob?.id);
      setJob(jobDetails);
    } catch (error) {
      console.error('Error fetching job details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveJob = async () => {
    try {
      setSaving(true);
      if (isSaved) {
        await unsaveJob(job?.id);
        setIsSaved(false);
      } else {
        await saveJob(job?.id);
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error saving job:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleApply = () => {
    if (job?.application_url) {
      window.open(job?.application_url, '_blank');
    } else {
      setShowApplicationModal(true);
    }
  };

  const formatSalary = (min, max, currency = 'USD') => {
    if (!min && !max) return 'Not specified';
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    
    if (min && max) {
      return `${formatter?.format(min)} - ${formatter?.format(max)}`;
    }
    return formatter?.format(min || max);
  };

  const formatJobType = (type) => {
    return type?.replace('_', ' ')?.split(' ')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ') || '';
  };

  const formatCategory = (category) => {
    return category?.replace('_', ' ')?.split(' ')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ') || '';
  };

  const formatExperienceLevel = (level) => {
    return level?.replace('_', ' ')?.split(' ')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ') || '';
  };

  const isDeadlineNear = (deadline) => {
    if (!deadline) return false;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffDays = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading job details...</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                      {job?.title}
                    </h1>
                    <p className="text-gray-600 mb-4">
                      {job?.user_profiles?.organization || job?.user_profiles?.full_name}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span>{job?.location}</span>
                        {job?.is_remote && (
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Remote
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>{formatJobType(job?.job_type)}</span>
                      </div>
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        <span>{formatExperienceLevel(job?.experience_level)}</span>
                      </div>
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        <span>{formatSalary(job?.salary_min, job?.salary_max, job?.currency)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={onClose}
                    className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={handleApply}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    {job?.application_url ? (
                      <>
                        <ExternalLink className="w-4 h-4" />
                        Apply Now
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Apply
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={handleSaveJob}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    {isSaved ? (
                      <>
                        <Bookmark className="w-4 h-4 fill-current" />
                        Saved
                      </>
                    ) : (
                      <>
                        <BookmarkPlus className="w-4 h-4" />
                        Save Job
                      </>
                    )}
                  </button>
                </div>

                {/* Deadline Warning */}
                {job?.application_deadline && isDeadlineNear(job?.application_deadline) && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-red-600 mr-2" />
                      <span className="text-red-800 text-sm font-medium">
                        Application deadline: {new Date(job?.application_deadline)?.toLocaleDateString()} (Soon!)
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Main Content */}
                  <div className="lg:col-span-2 space-y-8">
                    {/* Job Description */}
                    <section>
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">Job Description</h2>
                      <div className="prose max-w-none text-gray-700 whitespace-pre-wrap">
                        {job?.description}
                      </div>
                    </section>

                    {/* Responsibilities */}
                    {job?.responsibilities?.length > 0 && (
                      <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Responsibilities</h2>
                        <ul className="list-disc list-inside space-y-2 text-gray-700">
                          {job?.responsibilities?.map((responsibility, index) => (
                            <li key={index}>{responsibility}</li>
                          ))}
                        </ul>
                      </section>
                    )}

                    {/* Requirements */}
                    {job?.requirements?.length > 0 && (
                      <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Requirements</h2>
                        <ul className="list-disc list-inside space-y-2 text-gray-700">
                          {job?.requirements?.map((requirement, index) => (
                            <li key={index}>{requirement}</li>
                          ))}
                        </ul>
                      </section>
                    )}

                    {/* Qualifications */}
                    {job?.qualifications?.length > 0 && (
                      <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Qualifications</h2>
                        <ul className="list-disc list-inside space-y-2 text-gray-700">
                          {job?.qualifications?.map((qualification, index) => (
                            <li key={index}>{qualification}</li>
                          ))}
                        </ul>
                      </section>
                    )}

                    {/* Benefits */}
                    {job?.benefits?.length > 0 && (
                      <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Benefits</h2>
                        <ul className="list-disc list-inside space-y-2 text-gray-700">
                          {job?.benefits?.map((benefit, index) => (
                            <li key={index}>{benefit}</li>
                          ))}
                        </ul>
                      </section>
                    )}
                  </div>

                  {/* Sidebar */}
                  <div className="space-y-6">
                    {/* Job Info */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Information</h3>
                      <div className="space-y-3 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Category:</span>
                          <span className="ml-2 text-gray-600">{formatCategory(job?.category)}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Job Type:</span>
                          <span className="ml-2 text-gray-600">{formatJobType(job?.job_type)}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Experience:</span>
                          <span className="ml-2 text-gray-600">{formatExperienceLevel(job?.experience_level)}</span>
                        </div>
                        {job?.start_date && (
                          <div>
                            <span className="font-medium text-gray-700">Start Date:</span>
                            <span className="ml-2 text-gray-600">
                              {new Date(job?.start_date)?.toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-gray-700">Applications:</span>
                          <span className="ml-2 text-gray-600">{job?.application_count || 0}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Views:</span>
                          <span className="ml-2 text-gray-600">{job?.view_count || 0}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Posted:</span>
                          <span className="ml-2 text-gray-600">
                            {new Date(job?.created_at)?.toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Skills Required */}
                    {job?.skills_required?.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills Required</h3>
                        <div className="flex flex-wrap gap-2">
                          {job?.skills_required?.map((skill, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Company Info */}
                    {job?.user_profiles && (
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">About the Employer</h3>
                        <div className="space-y-3">
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {job?.user_profiles?.organization || job?.user_profiles?.full_name}
                            </h4>
                            {job?.user_profiles?.title && (
                              <p className="text-sm text-gray-600">{job?.user_profiles?.title}</p>
                            )}
                          </div>
                          {job?.user_profiles?.bio && (
                            <p className="text-sm text-gray-700">{job?.user_profiles?.bio}</p>
                          )}
                          {job?.contact_email && (
                            <div className="pt-2">
                              <span className="text-sm text-gray-600">Contact: </span>
                              <a
                                href={`mailto:${job?.contact_email}`}
                                className="text-sm text-blue-600 hover:text-blue-800"
                              >
                                {job?.contact_email}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Application Modal */}
      {showApplicationModal && (
        <ApplicationModal
          job={job}
          isOpen={showApplicationModal}
          onClose={() => setShowApplicationModal(false)}
        />
      )}
    </>
  );
};

export default JobDetailModal;